import { useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/v2/EmptyState";
import {
  useAddBookingComment,
  useBookingComments,
  type BookingComment,
} from "@/lib/booking-attachments";

/**
 * Comments tab — Pencil `edhwT`. Internal staff comments + composer,
 * backed by the `/api/v1/bookings/{id}/comments` endpoints. Comments
 * persist server-side, sync across teammates, and survive refresh /
 * sign-out.
 */
export function BookingCommentsTab({ bookingId }: { bookingId: string }) {
  const list = useBookingComments(bookingId);
  const add = useAddBookingComment(bookingId);
  const [draft, setDraft] = useState("");

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    add.mutate(body, {
      onSuccess: () => setDraft(""),
    });
  };

  const comments = list.data?.entries ?? [];

  return (
    <div className="flex flex-col h-full -mx-5 -my-5">
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {list.isLoading && (
          <p className="text-sm text-muted-foreground">Loading comments…</p>
        )}
        {list.isError && (
          <p className="text-sm text-destructive">
            {(list.error as Error).message}
          </p>
        )}
        {!list.isLoading && !list.isError && comments.length === 0 ? (
          <EmptyState
            icon={MessageCircle}
            title="No comments yet"
            description="Internal notes are visible only to your team. Add one to start a thread."
          />
        ) : (
          <ol className="space-y-4">
            {comments.map((c: BookingComment) => (
              <li key={c.id} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center text-[11px] font-medium shrink-0">
                  {initialsFromEmail(c.authorEmail)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <p className="text-sm font-medium truncate">
                      {displayNameFromEmail(c.authorEmail)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {timeAgo(c.createdAt)}
                    </p>
                  </div>
                  <p className="text-sm mt-0.5 whitespace-pre-wrap">{c.body}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="border-t border-border px-5 py-3 flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Add a comment… (⌘+Enter to send)"
          disabled={add.isPending}
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        />
        <Button
          size="sm"
          onClick={send}
          disabled={!draft.trim() || add.isPending}
        >
          <Send className="h-3.5 w-3.5 mr-1" />
          {add.isPending ? "Sending…" : "Send"}
        </Button>
      </div>
    </div>
  );
}

function initialsFromEmail(email: string): string {
  return email
    .split("@")[0]
    .split(/[._-]/)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");
}

function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0];
  return local
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
